import {
  SWAP_TICKET_DIRECTIONS,
  returnSwapDirectionChanges,
} from "../helper/swapTicketHelper";
import { returnSwapTemplateChanges } from "../helper/swapTemplateRule";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import SwapTemplateSelect from "../ticketFields/SwapTemplateSelect";
import DirectionSelect from "../ticketFields/DirectionSelect";
import NominalInput from "../ticketFields/NominalInput";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketRow from "../ticketFields/TicketRow";
import AllocationSplits from "../ticketFields/AllocationSplits";
import TicketExternalIdRow from "../ticketFields/TicketExternalIdRow";
import { useSwapTicket } from "./useSwapTicket";
import { returnLegHolidayCalendars } from "../helper/holidayCalendarsHelper";

// The swap fields a Trade Creation leg row shows inline. Dates and legs live in
// SwapDetailFields.

const SwapCoreFields = ({ ticketId, highlightedKeys }: ITicketFieldsProps) => {
  const { ticket, updateSwapTicket, isPerLegNominal, requiredKeys } =
    useSwapTicket(ticketId);

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  return (
    <div
      data-testid="SwapCoreFieldsContainer"
      id="SwapCoreFieldsContainer"
      className="flex flex-col gap-2"
    >
      {/* Template swap: pre-fills legs, tenor and settlement (LQC-2939). Filtered by
          Direction, so Buy / Sell Inflation lists ZCS only (LQC-2991). */}
      <TicketRow label="Security" testId="SwapTicketTemplateRow">
        <SwapTemplateSelect
          testId="SwapTicketTemplateInput"
          value={ticket.templateName}
          direction={ticket.direction}
          onSelect={(template) =>
            updateSwapTicket(returnSwapTemplateChanges(ticket, template))
          }
        />
      </TicketRow>

      <TicketRow
        label="Nominal"
        testId="SwapTicketNominalRow"
        isHighlighted={isMissing("nominal")}
        isRequired={isRequired("nominal")}
      >
        {/* XCS/XBS capture each leg's nominal in the details instead (LQC-2971). */}
        <NominalInput
          testId="SwapTicketNominalInput"
          value={ticket.nominal}
          onCommit={(nominal) => updateSwapTicket({ nominal })}
          isDisabled={isPerLegNominal}
        />
      </TicketRow>

      <AllocationSplits
        testIdPrefix="SwapTicket"
        portfolioCode={ticket.portfolioCode}
        onPortfolioChange={(portfolioCode) => updateSwapTicket({ portfolioCode })}
        isPortfolioRequired={isRequired("portfolioCode")}
        total={ticket.nominal}
        splits={ticket.additionalAllocations}
        sizeKind="nominal"
        highlightedKeys={highlightedKeys}
        onChange={(additionalAllocations) => updateSwapTicket({ additionalAllocations })}
      />

      <TicketRow
        label="Direction"
        testId="SwapTicketDirectionRow"
        isHighlighted={isMissing("direction")}
        isRequired={isRequired("direction")}
      >
        <DirectionSelect
          testId="SwapTicketDirectionInput"
          value={ticket.direction}
          directions={SWAP_TICKET_DIRECTIONS}
          // Buy / Sell Inflation re-shape the legs as a ZCS (LQC-2991).
          onChange={(direction) =>
            updateSwapTicket(returnSwapDirectionChanges(ticket, direction))
          }
        />
      </TicketRow>

      <TicketRow
        label="Settlement Date"
        testId="SwapTicketSettlementDateRow"
        isHighlighted={isMissing("settlementDate")}
        isRequired={isRequired("settlementDate")}
      >
        <TicketDateInput
          testId="SwapTicketSettlementDateInput"
          holidayCalendars={returnLegHolidayCalendars(ticket)}
          value={ticket.settlementDate}
          onChange={(settlementDate) => updateSwapTicket({ settlementDate })}
        />
      </TicketRow>

      <TicketExternalIdRow
        testIdPrefix="SwapTicket"
        value={ticket.transactionNumber}
        onChange={(transactionNumber) => updateSwapTicket({ transactionNumber })}
      />
    </div>
  );
};

export default SwapCoreFields;
